import {useState} from 'react';
import {Collapse} from 'react-bootstrap';
import clsx from 'clsx';
import {OrderMessage} from '../../../../interfaces/order';
import CardComponent from '../../../../common/components/CardComponent';
import {useIntl} from 'react-intl';
import {durationSince} from '../../../../common/tools/date';
import {truncateText} from '../../../../common/tools/text';

interface OrderTrackingConversationProps {
    messages: OrderMessage[];
}

const senderLabel = (message: OrderMessage, intl: ReturnType<typeof useIntl>): string =>
    message.type === 'reply'
        ? intl.formatMessage({id: 'ORDER_TRACKING.EMAILS.YOUR_REPLY'})
        : message.sender;

interface ExpandableMessageProps {
    message: OrderMessage;
    className?: string;
}

const ExpandableMessage = ({message, className}: ExpandableMessageProps): JSX.Element => {
    const [open, setOpen] = useState(false);
    const intl = useIntl();

    return (
        <div>
            <div
                className={clsx(className, {'d-none': open})}
                dangerouslySetInnerHTML={{__html: truncateText(message.content)}}
            ></div>
            <Collapse in={open}>
                <div
                    className={className}
                    style={{whiteSpace: 'pre-line'}}
                    dangerouslySetInnerHTML={{__html: message.content}}
                ></div>
            </Collapse>
            <span
                className="text-celeste-primary fw-bold fs-8 cursor-pointer d-inline-flex align-items-center gap-1"
                onClick={() => setOpen(!open)}
            >
                {open
                    ? intl.formatMessage({id: 'FOLD'})
                    : intl.formatMessage({id: 'ORDER_TRACKING.EMAILS.MORE'})}
                <i className={clsx('bi', open ? 'bi-chevron-up' : 'bi-chevron-down')}></i>
            </span>
        </div>
    );
};

const OrderTrackingConversation = ({messages}: OrderTrackingConversationProps): JSX.Element => {
    const intl = useIntl();
    const title = intl.formatMessage({id: 'ORDER_TRACKING.EMAILS.TITLE'});
    const sorted = [...messages].sort(
        (a, b) => new Date(b.time).getTime() - new Date(a.time).getTime()
    );

    return (
        <CardComponent title={title}>
            <div className="vh-50 overflow-scroll">
                {sorted.length ? (
                    sorted.map((message, index) => {
                        const isReply = message.type === 'reply';
                        return (
                            <div
                                className={clsx('d-flex align-items-start mb-6', {
                                    'justify-content-end': isReply,
                                })}
                                key={message.time + index}
                            >
                                {!isReply && (
                                    <div className="symbol symbol-40px me-3 flex-shrink-0">
                                        <span
                                            className="symbol-label"
                                            style={{
                                                backgroundColor: 'white',
                                                backgroundImage:
                                                    'url(/media/logos/LOGO_CELESTE_Bleu_etoile_seule.png)',
                                            }}
                                        />
                                    </div>
                                )}
                                <div className="flex-grow-1" style={{maxWidth: '92%'}}>
                                    <div
                                        className={clsx('d-flex align-items-center gap-2 mb-1', {
                                            'justify-content-end': isReply,
                                        })}
                                    >
                                        <span className="fw-bold fs-7 text-dark">
                                            {senderLabel(message, intl)}
                                        </span>
                                        <span className="text-muted fs-8">
                                            {durationSince(message.time.replace(' ', 'T'))}
                                        </span>
                                    </div>
                                    <div
                                        className={clsx('rounded p-4', {
                                            'bg-celeste-primary bg-opacity-10': isReply,
                                            'bg-light': !isReply,
                                        })}
                                    >
                                        <ExpandableMessage
                                            message={message}
                                            className="text-gray-800 fw-semibold"
                                        />
                                    </div>
                                </div>
                                {isReply && (
                                    <div className="symbol symbol-light-success symbol-40px ms-3 flex-shrink-0">
                                        <div className="symbol-label font-weight-bolder">
                                            {intl.formatMessage({id: 'YOU'})}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })
                ) : (
                    <p className="text-center">
                        {intl.formatMessage({id: 'ORDER_TRACKING.EMAILS.EMPTY'})}
                    </p>
                )}
            </div>
        </CardComponent>
    );
};

export default OrderTrackingConversation;
